import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Star, Download, Eye, Heart, ShoppingCart, 
  ExternalLink, Image, Video, Calendar, User,
  Tag, Award, CheckCircle, X, ChevronLeft, ChevronRight,
  MessageCircle, Share2, Bookmark, FileText, TrendingUp, Search
} from 'lucide-react';
import { productAPI, promotionsAPI } from '../services/api';
import { getS3ImageUrl } from '../utils/apiUtils';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import WishlistButton from '../components/WishlistButton';
import SharePrintButtons from '../components/SharePrintButtons';
import { useNotificationHook } from '../hooks/useNotificationHook';
import { debounce } from 'lodash';

// CSS styles for product description
const productDescriptionStyles = `
  .product-description img {
    max-width: 100%;
    height: auto;
    margin: 0.5rem;
    display: inline-block;
    vertical-align: top;
  }
  
  /* Support for side-by-side images */
  .product-description .image-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 1rem 0;
  }
  
  .product-description .image-container img {
    flex: 1;
    min-width: 200px;
    max-width: calc(50% - 0.25rem);
  }
  
  /* Support for clickable images */
  .product-description a img {
    cursor: pointer;
    transition: opacity 0.2s ease;
  }
  
  .product-description a img:hover {
    opacity: 0.8;
  }
  
  .product-description a {
    color: #3b82f6;
    text-decoration: underline;
  }
  
  .dark .product-description a {
    color: #60a5fa;
  }
`;

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInWishlist, addToWishlist, removeFromWishlist } = useCart();
  const { formatPrice } = useCurrency();
  const { notify } = useNotificationHook();

  // Inject CSS styles for product description
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = productDescriptionStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Helper functions to check if license has details
  const hasRegularLicenseDetails = (product) => {
    if (!product) return false;
    return (
      (product.regular_license_points && 
       (Array.isArray(product.regular_license_points) ? product.regular_license_points.length > 0 : product.regular_license_points.trim() !== '')) ||
      (product.regular_license_price && product.regular_license_price > 0) ||
      (product.regular_license_original_price && product.regular_license_original_price > 0)
    );
  };

  // Compute price/original/discount triple from any two
  const computeTriple = (priceInput, originalInput, discountInput) => {
    const price = priceInput === '' || priceInput === null || priceInput === undefined ? null : Number(priceInput);
    const original = originalInput === '' || originalInput === null || originalInput === undefined ? null : Number(originalInput);
    const discount = discountInput === '' || discountInput === null || discountInput === undefined ? null : Number(discountInput);

    let outPrice = price;
    let outOriginal = original;
    let outDiscount = discount;

    if ((outPrice === null || isNaN(outPrice)) && outOriginal != null && !isNaN(outOriginal) && outDiscount != null && !isNaN(outDiscount)) {
      outPrice = +(outOriginal * (1 - outDiscount / 100)).toFixed(2);
    }
    if ((outDiscount === null || isNaN(outDiscount)) && outPrice != null && !isNaN(outPrice) && outOriginal != null && !isNaN(outOriginal) && outOriginal > 0) {
      outDiscount = +(((outOriginal - outPrice) / outOriginal) * 100).toFixed(2);
    }
    if ((outOriginal === null || isNaN(outOriginal)) && outPrice != null && !isNaN(outPrice) && outDiscount != null && !isNaN(outDiscount)) {
      const denom = (1 - outDiscount / 100);
      outOriginal = denom !== 0 ? +(outPrice / denom).toFixed(2) : outPrice;
    }

    return {
      price: outPrice ?? 0,
      original: outOriginal ?? 0,
      discount: outDiscount ?? 0,
    };
  };

  const hasExtendedLicenseDetails = (product) => {
    if (!product) return false;
    return (
      (product.extended_license_points && 
       (Array.isArray(product.extended_license_points) ? product.extended_license_points.length > 0 : product.extended_license_points.trim() !== '')) ||
      (product.extended_license_price && product.extended_license_price > 0) ||
      (product.extended_license_original_price && product.extended_license_original_price > 0)
    );
  };
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promotions, setPromotions] = useState([]);
  
  // Product selection state
  const [selectedLicense, setSelectedLicense] = useState('regular');
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [extendedSupport, setExtendedSupport] = useState(false);

  // Auto-select available license when product loads
  useEffect(() => {
    if (product) {
      const hasRegular = hasRegularLicenseDetails(product);
      const hasExtended = hasExtendedLicenseDetails(product);
      
      if (hasRegular && hasExtended) {
        // Both available, keep current selection or default to regular
        setSelectedLicense(selectedLicense);
      } else if (hasRegular) {
        // Only regular available
        setSelectedLicense('regular');
      } else if (hasExtended) {
        // Only extended available
        setSelectedLicense('extended');
      }
    }
  }, [product]);
  
  // UI state
  const [selectedImage, setSelectedImage] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showScreenshotsModal, setShowScreenshotsModal] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState(0);
  
  // Carousel state
  const [featuredCarouselIndex, setFeaturedCarouselIndex] = useState(0);
  const [relatedCarouselIndex, setRelatedCarouselIndex] = useState(0);
  const [topBannerIndex, setTopBannerIndex] = useState(0);
  const [bottomBannerIndex, setBottomBannerIndex] = useState(0);
  const [topBannerPromotions, setTopBannerPromotions] = useState([]);

  // Breadcrumb search state
  const [breadcrumbSearch, setBreadcrumbSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const fetchSuggestions = useCallback(
    debounce(async (term) => {
      if (!term || !term.trim()) {
        setSuggestions([]);
        return;
      }
      try {
        const response = await productAPI.getAll({ status: 'active', search: term, page: 1, limit: 5 });
        if (response?.data?.success) {
          const productsData = response.data.data.products || [];
          setSuggestions(productsData.map(p => ({ id: p.id, name: p.name, slug: p.slug })));
        }
      } catch (e) {
        setSuggestions([]);
      }
    }, 250)
  , []);

  const handleBreadcrumbSearchChange = (e) => {
    const val = e.target.value;
    setBreadcrumbSearch(val);
    setShowSuggestions(true);
    fetchSuggestions(val);
  };

  const submitBreadcrumbSearch = () => {
    if (breadcrumbSearch && breadcrumbSearch.trim()) {
      navigate(`/products?q=${encodeURIComponent(breadcrumbSearch.trim())}`);
    }
  };

  // Auto-scroll for banner promotions
  useEffect(() => {
    if (topBannerPromotions.length <= 1) return;

    const topBannerInterval = setInterval(() => {
      setTopBannerIndex(prev => (prev + 1) % topBannerPromotions.length);
    }, 5000); // Change every 5 seconds

    return () => {
      clearInterval(topBannerInterval);
    };
  }, [topBannerPromotions.length]);

  // Auto-scroll for bottom banner promotions
  useEffect(() => {
    if (promotions.length <= 1) return;

    const bottomBannerInterval = setInterval(() => {
      setBottomBannerIndex(prev => (prev + 1) % promotions.length);
    }, 7000); // Change every 7 seconds (slightly different timing)

    return () => {
      clearInterval(bottomBannerInterval);
    };
  }, [promotions.length]);

  // Fetch promotions for product-detail page
  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        console.log('🔍 Fetching promotions with params:', { page: 'product-detail' });
        
        const response = await promotionsAPI.getAll({
          page: 'product-detail'
        });
        
        console.log('📡 Promotions API response:', response);
        
        if (response?.data?.success) {
          console.log('✅ Raw promotions from API:', response.data.data.promotions);
          
          const activePromotions = response.data.data.promotions?.filter(promotion => {
            console.log('🔍 Checking promotion:', {
              id: promotion.id,
              title: promotion.title,
              type: promotion.type,
              is_active: promotion.is_active,
              show_on_pages: promotion.show_on_pages,
              valid_from: promotion.valid_from,
              valid_until: promotion.valid_until
            });
            
            // Check if promotion is active
            if (!promotion.is_active) {
              console.log('❌ Promotion not active:', promotion.id);
              return false;
            }
            
            // Check if promotion is for product-detail page
            if (!promotion.show_on_pages?.includes('product-detail')) {
              console.log('❌ Promotion not for product-detail page:', promotion.id);
              return false;
            }
            
            // Allow all promotion types for product detail page
            // (Top banner will filter to only banner/coupon, bottom section will show all types)
            
            // Check validity dates
            const now = new Date();
            if (promotion.valid_from && new Date(promotion.valid_from) > now) {
              console.log('❌ Promotion not yet valid:', promotion.id);
              return false;
            }
            if (promotion.valid_until && new Date(promotion.valid_until) < now) {
              console.log('❌ Promotion expired:', promotion.id);
              return false;
            }
            
            console.log('✅ Promotion passed all checks:', promotion.id);
            return true;
          }) || [];
          
          setPromotions(activePromotions);
          
          // Filter promotions for top banner (only banner and coupon types)
          const topBannerPromos = activePromotions.filter(promotion => 
            ['banner', 'coupon'].includes(promotion.type)
          );
          setTopBannerPromotions(topBannerPromos);
          
          console.log('🎯 Final active promotions for product-detail:', activePromotions);
          console.log('🎯 Top banner promotions:', topBannerPromos);
          
          // Debug video promotions specifically
          const videoPromotions = activePromotions.filter(p => p.type === 'video');
          console.log('🎥 Video promotions found:', videoPromotions);
          videoPromotions.forEach(video => {
            console.log('🎥 Video promotion details:', {
              id: video.id,
              title: video.title,
              video_url: video.video_url,
              is_active: video.is_active,
              show_on_pages: video.show_on_pages
            });
          });
        } else {
          console.log('❌ API response not successful:', response);
        }
      } catch (err) {
        console.error('❌ Error fetching promotions:', err);
      }
    };

    fetchPromotions();
  }, []);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await productAPI.getById(slug);
        
        if (response?.data?.success) {
          const { product, relatedProducts, featuredProducts, reviews } = response.data.data;
          
          // Debug logging
          console.log('🔍 Frontend Data Received:', {
            product: product?.name,
            relatedProducts: relatedProducts?.length || 0,
            featuredProducts: featuredProducts?.length || 0,
            reviews: reviews?.length || 0
          });
          
          console.log('🏆 Featured Products Details:', featuredProducts);
          
          setProduct(product);
          setRelatedProducts(relatedProducts || []);
          setFeaturedProducts(featuredProducts || []);
          setReviews(reviews || []);
        } else {
          setError('Product not found');
          notify.error('Product not found', 'Not Found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product');
        notify.error('Failed to load product', 'Loading Error');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  // Calculate total price
  const calculateTotalPrice = () => {
    if (!product) return 0;
    
    let total = 0;
    
    // Base license price - computed from available fields
    if (selectedLicense === 'regular' && hasRegularLicenseDetails(product)) {
      const rc = computeTriple(product.regular_license_price, product.regular_license_original_price, product.regular_license_discount);
      total += rc.price || 0;
    } else if (selectedLicense === 'extended' && hasExtendedLicenseDetails(product)) {
      const ec = computeTriple(product.extended_license_price, product.extended_license_original_price, product.extended_license_discount);
      total += ec.price || 0;
    }
    
    // Add selected addons (computed)
    if (product.addons && Array.isArray(product.addons)) {
      selectedAddons.forEach(addonId => {
        const addon = product.addons.find(a => a.id === addonId);
        if (addon) {
          const ac = computeTriple(addon.price, addon.original_price, addon.discount_percentage);
          total += ac.price || 0;
        }
      });
    }
    
    // Add extended support (computed)
    if (extendedSupport && (product.extended_support_price || product.extended_support_original_price || product.extended_support_discount)) {
      const sc = computeTriple(product.extended_support_price, product.extended_support_original_price, product.extended_support_discount);
      total += sc.price || 0;
    }
    
    // Debug logging
    console.log('Price calculation debug:', {
      selectedLicense,
      regularPrice: product.regular_license_price,
      extendedPrice: product.extended_license_price,
      supportPrice: product.extended_support_price,
      extendedSupport,
      total
    });
    
    return total;
  };



  // Handle WhatsApp order
  const handleWhatsAppOrder = () => {
    const totalPrice = calculateTotalPrice();
    const selectedAddonsText = selectedAddons.length > 0 
      ? `\nAddons: ${selectedAddons.map(id => {
          const addon = product.addons.find(a => a.id === id);
          return addon ? addon.name : '';
        }).join(', ')}`
      : '';
    
    const supportText = extendedSupport ? '\nExtended Support: Yes' : '';
    
    // Only include license info if licenses are available
    const licenseText = (hasRegularLicenseDetails(product) || hasExtendedLicenseDetails(product)) 
      ? `\nLicense: ${selectedLicense === 'regular' ? 'Regular' : 'Extended'}`
      : '';
    
    const message = `Hi! I'm interested in purchasing:

Product: ${product.name}${licenseText}${selectedAddonsText}${supportText}
Total Price: ${formatPrice(totalPrice)}

Can you please provide more details?`;
    
    const whatsappUrl = `https://wa.me/919515377622?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Handle addon selection
  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  // Render star rating
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  // Convert video URL to embed format
  const getEmbedVideoUrl = (url) => {
    if (!url) return '';
    
    // YouTube URL patterns
    const youtubePatterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/,
      /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]+)/
    ];
    
    // Vimeo URL patterns
    const vimeoPatterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/
    ];
    
    // Check for YouTube
    for (const pattern of youtubePatterns) {
      const match = url.match(pattern);
      if (match) {
        return `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`;
      }
    }
    
    // Check for Vimeo
    for (const pattern of vimeoPatterns) {
      const match = url.match(pattern);
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}?autoplay=1`;
      }
    }
    
    // If it's already an embed URL, return as is
    if (url.includes('/embed/')) {
      return url.includes('?') ? `${url}&autoplay=1` : `${url}?autoplay=1`;
    }
    
    // For other video URLs, return as is
    return url;
  };

  // Carousel navigation functions
  const nextSlide = (currentIndex, totalItems, setIndex) => {
    const maxIndex = Math.max(0, totalItems - 3);
    setIndex(currentIndex >= maxIndex ? 0 : currentIndex + 1);
  };

  const prevSlide = (currentIndex, totalItems, setIndex) => {
    const maxIndex = Math.max(0, totalItems - 3);
    setIndex(currentIndex <= 0 ? maxIndex : currentIndex - 1);
  };

  // Calculate transform for smooth sliding animation
  const getCarouselTransform = (index, totalItems) => {
    const maxIndex = Math.max(0, totalItems - 3);
    const normalizedIndex = index > maxIndex ? maxIndex : index;
    return `translateX(-${normalizedIndex * (100 / 3)}%)`;
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {error || 'Product not found'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The product you're looking for doesn't exist or has been removed.
          </p>
          <Link 
            to="/products" 
            className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-500 shadow-glow"
          >
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideOut {
          from {
            opacity: 1;
            transform: translateX(0);
          }
          to {
            opacity: 0;
            transform: translateX(-20px);
          }
        }
      `}</style>
      {/* Modern Breadcrumb */}
      <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-visible z-40">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-12 py-4 relative z-50">
          <div className="flex items-center justify-between">
            <nav className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition-colors">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <Link to="/products" className="text-gray-600 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 transition-colors">
              Products
            </Link>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-gray-900 dark:text-white font-medium">
              {product.name}
            </span>
            </nav>
            <div className="relative w-full max-w-xs hidden sm:block z-[100]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={breadcrumbSearch}
                onChange={handleBreadcrumbSearchChange}
                onKeyDown={(e) => { if (e.key === 'Enter') submitBreadcrumbSearch(); }}
                onFocus={() => breadcrumbSearch && setShowSuggestions(true)}
                placeholder="Search products..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/90 dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[100]">
                  <ul className="max-h-64 overflow-auto">
                    {suggestions.map(s => (
                      <li key={s.id}>
                        <button
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => navigate(`/products?q=${encodeURIComponent(s.name)}`)}
                        >
                          {s.name}
                        </button>
                      </li>
                    ))}
                    <li>
                      <button
                        className="w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={submitBreadcrumbSearch}
                      >
                        View all results
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Banner Promotions */}
      {topBannerPromotions.length > 0 && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="relative">
              {/* Current Promotion */}
              {topBannerPromotions[topBannerIndex] && (
                <div 
                  key={topBannerPromotions[topBannerIndex].id} 
                  className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 transition-all duration-500 ease-in-out"
                >
                  <div className="flex items-center gap-4">
                    {/* Standardized Image/Video Space */}
                    <div className="flex-shrink-0 w-16 h-16">
                      {/* Banner Image */}
                      {topBannerPromotions[topBannerIndex].type === 'banner' && topBannerPromotions[topBannerIndex].image_url && (
                        <img 
                          src={topBannerPromotions[topBannerIndex].image_url} 
                          alt={topBannerPromotions[topBannerIndex].title}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      )}
                      
                      {/* Video Display */}
                      {topBannerPromotions[topBannerIndex].type === 'video' && topBannerPromotions[topBannerIndex].video_url && (
                        <div className="w-full h-full bg-gray-800 rounded-lg overflow-hidden">
                          <video 
                            src={topBannerPromotions[topBannerIndex].video_url}
                            className="w-full h-full object-cover"
                            controls
                            muted
                            loop
                          >
                            Your browser does not support the video tag.
                          </video>
                        </div>
                      )}
                      
                      {/* HTML Content Thumbnail */}
                      {topBannerPromotions[topBannerIndex].type === 'html' && (
                        <div className="w-full h-full bg-gray-800 rounded-lg overflow-hidden">
                          <div 
                            className="w-full h-full p-2 text-white text-xs"
                            dangerouslySetInnerHTML={{ __html: topBannerPromotions[topBannerIndex].html_content }}
                          />
                        </div>
                      )}
                      
                      {/* Coupon Code Thumbnail */}
                      {topBannerPromotions[topBannerIndex].type === 'coupon' && (
                        <div className="w-full h-full bg-green-600 rounded-lg flex items-center justify-center p-2">
                          <div className="text-center">
                            <div className="text-white font-mono font-bold text-sm">
                              {topBannerPromotions[topBannerIndex].coupon_code}
                            </div>
                            {topBannerPromotions[topBannerIndex].discount_percentage > 0 && (
                              <div className="text-white/90 text-xs mt-1">
                                {topBannerPromotions[topBannerIndex].discount_percentage}% OFF
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      {/* Standardized Title */}
                      <h3 className="text-white font-semibold text-lg mb-1">
                        {topBannerPromotions[topBannerIndex].title}
                      </h3>
                      
                      {/* Standardized Description */}
                      {topBannerPromotions[topBannerIndex].description && (
                        <p className="text-white/90 text-sm mb-2">
                          {topBannerPromotions[topBannerIndex].description}
                        </p>
                      )}
                      
                      {/* Type-specific Additional Content */}
                      {topBannerPromotions[topBannerIndex].type === 'video' && topBannerPromotions[topBannerIndex].video_url && (
                        <div className="bg-white/20 rounded-lg p-3 inline-block">
                          <div className="flex items-center gap-2">
                            <Video className="w-4 h-4 text-white" />
                            <span className="text-white/80 text-sm">Video Promotion</span>
                          </div>
                        </div>
                      )}
                      
                      {/* HTML Content Preview */}
                      {topBannerPromotions[topBannerIndex].type === 'html' && topBannerPromotions[topBannerIndex].html_content && (
                        <div className="bg-white/20 rounded-lg p-3 inline-block">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-white" />
                            <span className="text-white/80 text-sm">HTML Content</span>
                          </div>
                          <div 
                            className="text-white/90 text-sm mt-1 max-w-md line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: promotions[topBannerIndex].html_content }}
                          />
                        </div>
                      )}
                      

                    </div>
                    
                    <div className="flex-shrink-0">
                      <Tag className="w-5 h-5 text-white/70" />
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Controls */}
              {topBannerPromotions.length > 1 && (
                <div className="absolute top-1/2 transform -translate-y-1/2 flex justify-between w-full px-4">
                  <button
                    onClick={() => setTopBannerIndex(prev => prev > 0 ? prev - 1 : topBannerPromotions.length - 1)}
                    className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setTopBannerIndex(prev => (prev + 1) % topBannerPromotions.length)}
                    className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Dots Indicator */}
              {topBannerPromotions.length > 1 && (
                <div className="flex justify-center mt-4 space-x-2">
                  {topBannerPromotions.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setTopBannerIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === topBannerIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 sm:gap-12">
          {/* Left Column - Product Images & Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Product Images */}
            <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20">
              <div className="relative aspect-video bg-gray-100 dark:bg-gray-700 rounded-t-3xl overflow-hidden">
                {product.image ? (
                  <img 
                    src={getS3ImageUrl(product.image)} 
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <Image className="w-16 h-16" />
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="p-4 sm:p-8 space-y-4">
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {product.demo_url && (
                    <button 
                      onClick={() => window.open(product.demo_url, '_blank')}
                      className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:scale-105 text-white rounded-2xl transition-all duration-300 shadow-lg w-full sm:w-auto"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Live Preview
                    </button>
                  )}
                  
                  {product.screenshots && product.screenshots.length > 0 && (
                    <button 
                      onClick={() => setShowScreenshotsModal(true)}
                      className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-gray-600 hover:bg-gray-700 hover:scale-105 text-white rounded-2xl transition-all duration-300 shadow-lg w-full sm:w-auto"
                    >
                      <Image className="w-4 h-4" />
                      Screenshots
                    </button>
                  )}
                  
                  {product.video_url && (
                    <button 
                      onClick={() => setShowVideoModal(true)}
                      className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-red-600 hover:bg-red-700 hover:scale-105 text-white rounded-2xl transition-all duration-300 shadow-lg w-full sm:w-auto"
                    >
                      <Video className="w-4 h-4" />
                      Video
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Full Description */}
            {product.full_description && (
              <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Description
                </h3>
                <div 
                  className="prose prose-gray dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 product-description"
                  dangerouslySetInnerHTML={{ __html: product.full_description }}
                />
              </div>
            )}

            {/* Featured Products */}
            {console.log('🎯 Rendering Featured Products Section:', { featuredProducts: featuredProducts.length, featuredProducts })}
            <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Award className="w-6 h-6 text-yellow-500" />
                Featured Products ({featuredProducts.length})
              </h3>
              {featuredProducts.length > 0 ? (
                <div className="relative">
                  <div className="flex gap-6 overflow-hidden">
                    {featuredProducts.slice(featuredCarouselIndex, featuredCarouselIndex + 3).map((featuredProduct, index) => (
                      <div 
                        key={featuredProduct.id} 
                        className="flex-shrink-0 w-full sm:w-1/2 lg:w-1/3 transform transition-all duration-700 ease-in-out hover:scale-105 animate-slide-in"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <Link 
                          to={`/product/${featuredProduct.slug}`}
                          className="group block"
                        >
                          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl p-6 hover:shadow-xl transition-all duration-300 border border-white/20 hover:scale-105">
                            <div className="aspect-video bg-gray-200 dark:bg-gray-600 rounded-xl mb-4 overflow-hidden">
                              {featuredProduct.image ? (
                                <img 
                                  src={getS3ImageUrl(featuredProduct.image)} 
                                  alt={featuredProduct.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <Image className="w-8 h-8" />
                                </div>
                              )}
                            </div>
                            <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">
                              {featuredProduct.name}
                            </h4>
                            <div className="flex items-center justify-between mt-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                                  {formatPrice(featuredProduct.regular_license_price)}
                                </span>
                                {featuredProduct.regular_license_original_price && featuredProduct.regular_license_original_price > featuredProduct.regular_license_price && (
                                  <span className="text-xs text-gray-500 line-through">
                                    {formatPrice(featuredProduct.regular_license_original_price)}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-yellow-400 fill-current" />
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  {featuredProduct.rating}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                  
                  {/* Navigation Buttons */}
                  {featuredProducts.length > 3 && (
                    <>
                      <button
                        onClick={() => prevSlide(featuredCarouselIndex, featuredProducts.length, setFeaturedCarouselIndex)}
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-10 border border-white/20"
                      >
                        <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300 transition-colors duration-300" />
                      </button>
                      <button
                        onClick={() => nextSlide(featuredCarouselIndex, featuredProducts.length, setFeaturedCarouselIndex)}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-10 border border-white/20"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300 transition-colors duration-300" />
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                  No featured products available at the moment.
                </p>
              )}
            </div>



            {/* Related Products */}
            <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Tag className="w-6 h-6 text-green-500" />
                Related Products ({relatedProducts.length})
              </h3>
              {relatedProducts.length > 0 ? (
                <div className="relative">
                  <div className="flex gap-6 overflow-hidden transition-transform duration-500 ease-in-out">
                    {relatedProducts.slice(relatedCarouselIndex, relatedCarouselIndex + 3).map((relatedProduct, index) => (
                      <div 
                        key={relatedProduct.id} 
                        className="flex-shrink-0 w-full sm:w-1/2 lg:w-1/3 transform transition-all duration-700 ease-in-out hover:scale-105 animate-slide-in"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <Link 
                          to={`/product/${relatedProduct.slug}`}
                          className="group block"
                        >
                          <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl p-6 hover:shadow-xl transition-all duration-300 border border-white/20 hover:scale-105">
                            <div className="aspect-video bg-gray-200 dark:bg-gray-600 rounded-xl mb-4 overflow-hidden">
                              {relatedProduct.image ? (
                                <img 
                                  src={getS3ImageUrl(relatedProduct.image)} 
                                  alt={relatedProduct.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <Image className="w-8 h-8" />
                                </div>
                              )}
                            </div>
                            <h4 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">
                              {relatedProduct.name}
                            </h4>
                            <div className="flex items-center justify-between mt-3">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                                  {formatPrice(relatedProduct.regular_license_price)}
                                </span>
                                {relatedProduct.regular_license_original_price && relatedProduct.regular_license_original_price > relatedProduct.regular_license_price && (
                                  <span className="text-xs text-gray-500 line-through">
                                    {formatPrice(relatedProduct.regular_license_original_price)}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-yellow-400 fill-current" />
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  {relatedProduct.rating}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                  
                  {/* Navigation Buttons */}
                  {relatedProducts.length > 3 && (
                    <>
                      <button
                        onClick={() => prevSlide(relatedCarouselIndex, relatedProducts.length, setRelatedCarouselIndex)}
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-10 border border-white/20"
                      >
                        <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300 transition-colors duration-300" />
                      </button>
                      <button
                        onClick={() => nextSlide(relatedCarouselIndex, relatedProducts.length, setRelatedCarouselIndex)}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-10 border border-white/20"
                      >
                        <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300 transition-colors duration-300" />
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                  No related products available.
                </p>
              )}
            </div>
          </div>

          {/* Right Column - Product Details & Purchase */}
          <div className="space-y-8">
            {/* Product Info Card */}
            <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6">
                {product.name}
              </h1>
              
              {/* Rating & Stats */}
              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-2">
                  {renderStars(product.rating)}
                  <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">
                    ({product.reviews_count || reviews.length})
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <TrendingUp className="w-4 h-4" />
                  <span>{product.sales_count || product.downloads}</span>
                </div>
              </div>

              {/* Author & Version Info */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <User className="w-4 h-4" />
                  <span>by {product.author_name || product.author}</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                  <Tag className="w-4 h-4" />
                  <span>{product.category_name || product.category}</span>
                </div>
                {product.version_number && (
                  <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                    <Award className="w-4 h-4" />
                    <span>v{product.version_number}</span>
                  </div>
                )}
                {product.last_date && (
                  <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                    <Calendar className="w-4 h-4" />
                    <span>Last Updated: {new Date(product.last_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {/* License Selection - Only show if at least one license has details */}
              {(hasRegularLicenseDetails(product) || hasExtendedLicenseDetails(product)) && (
                <div className="mb-8">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-4">License Type</h3>
                  
                  {/* License Dropdown - Only show if both licenses are available */}
                  {hasRegularLicenseDetails(product) && hasExtendedLicenseDetails(product) && (
                    <div className="mb-6">
                      <select
                        value={selectedLicense}
                        onChange={(e) => setSelectedLicense(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-lg"
                      >
                        {hasRegularLicenseDetails(product) && (
                          <option value="regular" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Regular License</option>
                        )}
                        {hasExtendedLicenseDetails(product) && (
                          <option value="extended" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Extended License</option>
                        )}
                      </select>
                    </div>
                  )}
                  
                  {/* License Details */}
                  <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                    {selectedLicense === 'regular' && hasRegularLicenseDetails(product) ? (() => {
                      const rc = computeTriple(product.regular_license_price, product.regular_license_original_price, product.regular_license_discount);
                      return (
                        <div>
                          <div className="font-bold text-gray-900 dark:text-white mb-4">Regular License Details</div>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                            {Array.isArray(product.regular_license_points) ? (
                              <ul className="list-disc list-inside space-y-2">
                                {product.regular_license_points.map((point, index) => (
                                  <li key={index}>{point}</li>
                                ))}
                              </ul>
                            ) : (
                              <div>{product.regular_license_points}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                              {formatPrice(rc.price || 0)}
                            </span>
                            {rc.original > rc.price && (
                              <span className="text-sm text-gray-500 line-through">
                                {formatPrice(rc.original)}
                              </span>
                            )}
                            {rc.discount > 0 && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                                {rc.discount}% OFF
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })() : selectedLicense === 'extended' && hasExtendedLicenseDetails(product) ? (() => {
                      const ec = computeTriple(product.extended_license_price, product.extended_license_original_price, product.extended_license_discount);
                      return (
                        <div>
                          <div className="font-bold text-gray-900 dark:text-white mb-4">Extended License Details</div>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                            {Array.isArray(product.extended_license_points) ? (
                              <ul className="list-disc list-inside space-y-2">
                                {product.extended_license_points.map((point, index) => (
                                  <li key={index}>{point}</li>
                                ))}
                              </ul>
                            ) : (
                              <div>{product.extended_license_points}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                              {formatPrice(ec.price || 0)}
                            </span>
                            {ec.original > ec.price && (
                              <span className="text-sm text-gray-500 line-through">
                                {formatPrice(ec.original)}
                              </span>
                            )}
                            {ec.discount > 0 && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                                {ec.discount}% OFF
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })() : null}
                  </div>
                </div>
              )}

              {/* Extended Support */}
              {product.extended_support_text && (
                <div className="mb-8">
                  <label className="flex items-start gap-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={extendedSupport}
                      onChange={(e) => setExtendedSupport(e.target.checked)}
                      className="mt-1 w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <div className="flex-1">
                      <div className="font-bold text-gray-900 dark:text-white mb-2">Extended Support</div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                        {product.extended_support_text}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {formatPrice(product.extended_support_price)}
                        </span>
                        {product.extended_support_original_price && (
                          <span className="text-sm text-gray-500 line-through">
                            {formatPrice(product.extended_support_original_price)}
                          </span>
                        )}
                        {product.extended_support_discount > 0 && (
                          <span className="text-xs bg-red-100 text-red-600 px-3 py-1 rounded-full">
                            {product.extended_support_discount}% OFF
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                </div>
              )}

              {/* Addons */}
              {product.addons && product.addons.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-4">Addons</h3>
                  
                  {/* Addons Dropdown */}
                  <div className="mb-6">
                    <select
                      onChange={(e) => {
                        const addonId = parseInt(e.target.value);
                        if (addonId && !selectedAddons.includes(addonId)) {
                          toggleAddon(addonId);
                        }
                        e.target.value = ''; // Reset dropdown
                      }}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-lg"
                    >
                      <option value="" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">Select an addon...</option>
                      {product.addons.map((addon) => (
                        <option key={addon.id} value={addon.id} className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                          {addon.name} - {formatPrice(addon.price)}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Selected Addons */}
                  {selectedAddons.length > 0 && (
                    <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                      <div className="font-bold text-gray-900 dark:text-white mb-4">
                        Selected Addons ({selectedAddons.length})
                      </div>
                      <div className="space-y-4">
                        {selectedAddons.map((addonId) => {
                          const addon = product.addons.find(a => a.id === addonId);
                          if (!addon) return null;
                          
                          return (
                            <div key={addonId} className="flex items-center justify-between p-4 bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-xl border border-green-200 dark:border-green-700">
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-3 mb-2">
                                  <CheckCircle className="w-5 h-5 text-green-500" />
                                  {addon.name}
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-300 ml-8">
                                  {addon.short_description}
                                </div>
                              </div>
                              {(() => {
                                const ac = computeTriple(addon.price, addon.original_price, addon.discount_percentage);
                                return (
                                  <div className="flex flex-col gap-2">
                                    {ac.discount > 0 && (
                                      <span className="text-xs bg-red-100 text-red-600 px-3 py-1 rounded-full self-start">
                                        {ac.discount}% OFF
                                      </span>
                                    )}
                                    <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                      {formatPrice(ac.price || 0)}
                                    </span>
                                    {ac.original > ac.price && (
                                      <span className="text-sm text-gray-500 line-through">
                                        {formatPrice(ac.original)}
                                      </span>
                                    )}
                                  </div>
                                );
                              })()}
                              <div className="flex items-center gap-3">
                                {addon.product_slug && (
                                  <button
                                    onClick={() => window.open(`/product/${addon.product_slug}`, '_blank')}
                                    className="text-blue-600 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                    title="Open product details"
                                  >
                                    <ExternalLink className="w-5 h-5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => toggleAddon(addonId)}
                                  className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                  title="Remove addon"
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Total Price */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-8">
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-gray-900 dark:text-white">Total Price:</span>
                  <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                    {formatPrice(calculateTotalPrice())}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <button 
                  onClick={handleWhatsAppOrder}
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 hover:scale-105 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg"
                >
                  <MessageCircle className="w-6 h-6" />
                  Order on WhatsApp
                </button>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <button 
                    onClick={() => {
                      // Create cart item with all selected options
                      const cartItem = {
                        ...product,
                        selectedLicense,
                        selectedAddons,
                        extendedSupport,
                        calculatedPrice: calculateTotalPrice(),
                      originalPrice: selectedLicense === 'regular' && hasRegularLicenseDetails(product)
                        ? parseFloat(product.regular_license_price) || 0
                        : selectedLicense === 'extended' && hasExtendedLicenseDetails(product)
                        ? parseFloat(product.extended_license_price) || 0
                        : 0
                      };
                      addToCart(cartItem);
                      navigate('/cart');
                    }}
                    className="w-full sm:flex-1 flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:scale-105 text-white font-semibold rounded-lg transition-colors"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    Add to Cart
                  </button>
                </div>
                
                {/* Save, Share & Print Buttons */}
                <div className="flex justify-center mt-4 gap-2">
                  <WishlistButton 
                    item={product} 
                    itemType="product" 
                    showLabel={false}
                    size="default"
                  />
                  <SharePrintButtons 
                    item={product} 
                    itemType="product" 
                    showLabels={false}
                    size="default"
                  />
                </div>
                
                <button 
                  onClick={() => {
                    // Create cart item with all selected options
                    const cartItem = {
                      ...product,
                      selectedLicense,
                      selectedAddons,
                      extendedSupport,
                      calculatedPrice: calculateTotalPrice(),
                      originalPrice: selectedLicense === 'regular' && hasRegularLicenseDetails(product)
                        ? parseFloat(product.regular_license_price) || 0
                        : selectedLicense === 'extended' && hasExtendedLicenseDetails(product)
                        ? parseFloat(product.extended_license_price) || 0
                        : 0
                    };
                    addToCart(cartItem);
                    navigate('/checkout');
                  }}
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 hover:scale-105 text-white font-semibold rounded-lg transition-colors"
                >
                  <Download className="w-6 h-6" />
                  Buy Now
                </button>
              </div>
            </div>

            {/* Included Features */}
            {product.included && (
              <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20">
                <h3 className="font-bold text-gray-900 dark:text-white mb-6">What's Included</h3>
                <div className="space-y-3">
                  {product.included.split('\n').map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-300">{item.trim()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Tags Section */}
            {product.tags && Array.isArray(product.tags) && product.tags.length > 0 && (
              <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 border border-white/20">
                <h3 className="font-bold text-gray-900 dark:text-white mb-6">Tags & Keywords</h3>
                <div className="flex flex-wrap gap-3">
                  {product.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm rounded-full shadow-lg"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Banner Placement - Below Tags */}
            {promotions.length > 0 && (
              <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl overflow-hidden border border-white/20">
                <div className="relative">
                  {/* Current Promotion */}
                  {promotions[bottomBannerIndex] && (
                    <div key={promotions[bottomBannerIndex].id} className="relative transition-all duration-500 ease-in-out">
                      {/* Banner Image */}
                      {promotions[bottomBannerIndex].type === 'banner' && promotions[bottomBannerIndex].image_url && (
                        <div className="aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          <img 
                            src={promotions[bottomBannerIndex].image_url} 
                            alt={promotions[bottomBannerIndex].title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                          {/* Optional: Add a subtle overlay with promotion title */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-6">
                            <h4 className="text-white font-bold text-lg">
                              {promotions[bottomBannerIndex].title}
                            </h4>
                          </div>
                        </div>
                      )}
                      
                      {/* Video Promotion */}
                      {promotions[bottomBannerIndex].type === 'video' && promotions[bottomBannerIndex].video_url && (
                        <div className="aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          <div className="w-full h-full flex flex-col">
                            <div className="p-6 bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                              <h4 className="font-bold text-xl mb-3">{promotions[bottomBannerIndex].title}</h4>
                            </div>
                            <div className="flex-1 bg-black">
                              {(() => {
                                const videoUrl = promotions[bottomBannerIndex].video_url;
                                const embedUrl = getEmbedVideoUrl(videoUrl);
                                
                                console.log('🎥 Processing video URL:', {
                                  original: videoUrl,
                                  embed: embedUrl
                                });
                                
                                // Check if it's a YouTube or Vimeo URL
                                const isYouTube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
                                const isVimeo = videoUrl.includes('vimeo.com');
                                
                                console.log('🎥 Video type detection:', {
                                  isYouTube,
                                  isVimeo,
                                  isDirectFile: !isYouTube && !isVimeo
                                });
                                
                                // Validate video URL format
                                if (!videoUrl || videoUrl.trim() === '') {
                                  console.error('❌ Video URL is empty or invalid');
                                  return (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
                                      <div className="text-center">
                                        <Video className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                                        <p className="text-sm">No video URL provided</p>
                                      </div>
                                    </div>
                                  );
                                }
                                
                                if (isYouTube || isVimeo) {
                                  // Use iframe for YouTube/Vimeo
                                  return (
                                    <iframe
                                      src={embedUrl}
                                      className="w-full h-full"
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                      onError={(e) => {
                                        console.error('❌ Iframe failed to load:', embedUrl);
                                        console.error('❌ Iframe error:', e);
                                      }}
                                      onLoad={() => {
                                        console.log('✅ Iframe loaded successfully:', embedUrl);
                                      }}
                                    />
                                  );
                                } else {
                                  // Use video element for direct video files
                                  return (
                                    <video 
                                      src={videoUrl}
                                      className="w-full h-full object-cover"
                                      controls
                                      muted
                                      loop
                                      onError={(e) => {
                                        console.error('❌ Video failed to load:', videoUrl);
                                        console.error('❌ Video error:', e);
                                        // Show fallback content
                                        e.target.style.display = 'none';
                                        const fallback = e.target.parentNode.querySelector('.video-fallback');
                                        if (fallback) fallback.style.display = 'flex';
                                      }}
                                      onLoadStart={() => {
                                        console.log('🔍 Video loading started:', videoUrl);
                                      }}
                                      onCanPlay={() => {
                                        console.log('✅ Video can play:', videoUrl);
                                      }}
                                    >
                                      Your browser does not support the video tag.
                                    </video>
                                  );
                                }
                              })()}
                              
                              {/* Fallback content for when video fails to load */}
                              <div className="video-fallback hidden w-full h-full flex items-center justify-center bg-gray-800 text-white absolute inset-0">
                                <div className="text-center">
                                  <Video className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                                  <p className="text-sm">Video unavailable</p>
                                  <p className="text-xs text-gray-400 mt-1">Check the video URL</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* HTML Promotion */}
                      {promotions[bottomBannerIndex].type === 'html' && promotions[bottomBannerIndex].html_content && (
                        <div className="aspect-square bg-gray-100 dark:bg-gray-700 overflow-hidden">
                          <div className="w-full h-full flex flex-col">
                            <div className="p-6 bg-gradient-to-br from-purple-500 to-pink-600 text-white">
                              <h4 className="font-semibold text-lg">{promotions[bottomBannerIndex].title}</h4>
                            </div>
                            <div className="flex-1 p-6 overflow-y-auto">
                              <div 
                                className="prose prose-sm max-w-none text-gray-900 dark:text-gray-100"
                                dangerouslySetInnerHTML={{ __html: promotions[bottomBannerIndex].html_content }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Coupon Promotion */}
                      {promotions[bottomBannerIndex].type === 'coupon' && promotions[bottomBannerIndex].coupon_code && (
                        <div className="aspect-square bg-gradient-to-br from-green-500 to-green-600 overflow-hidden">
                          <div className="w-full h-full flex flex-col justify-center text-white p-6">
                            <div className="text-center">
                              <h4 className="font-semibold text-lg mb-3">{promotions[bottomBannerIndex].title}</h4>
                              {promotions[bottomBannerIndex].description && (
                                <p className="text-white/90 text-sm mb-4">
                                  {promotions[bottomBannerIndex].description}
                                </p>
                              )}
                              <div className="bg-white/20 rounded-lg p-6">
                                <span className="text-white/80 text-xs">Coupon Code</span>
                                <div className="text-white font-mono font-bold text-xl">
                                  {promotions[bottomBannerIndex].coupon_code}
                                </div>
                                {promotions[bottomBannerIndex].discount_percentage > 0 && (
                                  <span className="text-white/90 text-sm">
                                    {promotions[bottomBannerIndex].discount_percentage}% OFF
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Navigation Controls */}
                  {promotions.length > 1 && (
                    <div className="absolute top-1/2 transform -translate-y-1/2 flex justify-between w-full px-4">
                      <button
                        onClick={() => setBottomBannerIndex(prev => prev > 0 ? prev - 1 : promotions.length - 1)}
                        className="bg-black/20 hover:bg-black/30 text-white p-2 rounded-full transition-colors"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => setBottomBannerIndex(prev => (prev + 1) % promotions.length)}
                        className="bg-black/20 hover:bg-black/30 text-white p-2 rounded-full transition-colors"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  {/* Dots Indicator */}
                  {promotions.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                      {promotions.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setBottomBannerIndex(index)}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === bottomBannerIndex ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 pt-10 sm:pt-20"
          onClick={() => setShowImageModal(false)}
        >
          <div 
            className="relative w-full max-w-4xl max-h-[80vh] p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 sm:top-6 right-4 sm:right-6 text-white hover:text-gray-300 z-10 bg-black/50 rounded-full p-2 sm:p-3 backdrop-blur-sm transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={getS3ImageUrl(product.image)} 
              alt={product.name}
              className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      )}

      {/* Screenshots Modal */}
      {showScreenshotsModal && product.screenshots && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 pt-10 sm:pt-20"
          onClick={() => setShowScreenshotsModal(false)}
        >
          <div 
            className="relative w-full max-w-4xl max-h-[80vh] p-4 sm:p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowScreenshotsModal(false)}
              className="absolute top-4 sm:top-6 right-4 sm:right-6 text-white hover:text-gray-300 z-10 bg-black/50 rounded-full p-2 sm:p-3 backdrop-blur-sm transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="relative">
              <img 
                src={getS3ImageUrl(typeof product.screenshots[currentScreenshot] === 'string' ? 
                  product.screenshots[currentScreenshot] : 
                  product.screenshots[currentScreenshot]?.url)} 
                alt={`Screenshot ${currentScreenshot + 1}`}
                className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
              />
              
              {product.screenshots.length > 1 && (
                <>
                  <button 
                    onClick={() => setCurrentScreenshot(prev => prev > 0 ? prev - 1 : product.screenshots.length - 1)}
                    className="absolute left-3 sm:left-6 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 rounded-full p-2 sm:p-3 backdrop-blur-sm transition-colors"
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                  <button 
                    onClick={() => setCurrentScreenshot(prev => prev < product.screenshots.length - 1 ? prev + 1 : 0)}
                    className="absolute right-3 sm:right-6 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 bg-black/50 rounded-full p-2 sm:p-3 backdrop-blur-sm transition-colors"
                  >
                    <ChevronRight className="w-8 h-8" />
                  </button>
                </>
              )}
            </div>
            
            <div className="text-center mt-6 text-white bg-black/50 rounded-full px-6 py-3 backdrop-blur-sm inline-block">
              {currentScreenshot + 1} of {product.screenshots.length}
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && product.video_url && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-start justify-center z-50 pt-20"
          onClick={() => setShowVideoModal(false)}
        >
          <div 
            className="relative w-full max-w-6xl max-h-[80vh] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowVideoModal(false)}
              className="absolute -top-12 right-6 text-white hover:text-gray-300 z-10 bg-black/50 rounded-full p-3 backdrop-blur-sm transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <div className="w-full h-full aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              <iframe 
                src={getEmbedVideoUrl(product.video_url)}
                className="w-full h-full"
                frameBorder="0"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                title="Product Video"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;